import { Send } from "lucide-react";
import { useEffect, useState, type FC } from "react";
import { Link } from "react-router-dom";

import { OutgoingIntakesAPI } from "@/api/outgoingIntakesApi";
import type { OutgoingIntake } from "@/api/types/outgoingIntake";
import { Button } from "@/components/ui/button";
import { DropdownMenuItem } from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { asDate, displayStatus } from "@/features/requestsIde/model";
import { intakePath, REQUESTS_TAB } from "@/features/requestsIde/routes";
import { VISAVALUE } from "@/types/visa.type";

import { useMatterOptional } from "../context/MatterContext";
import { useCaseVisa } from "../useCaseVisa";

/** The visa's starter intake in the catalog (`visa_starter_templates.py`). */
export const visaStarterId = (visa: string): string =>
  `visa_${visa
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")}`;

/** Requests with the visa's starter intake ready to build. */
export const sendIntakePath = (clientId: string, visa: string): string => {
  const base = `/clients/${encodeURIComponent(clientId)}?tab=${REQUESTS_TAB}`;
  return visa ? `${base}&new=templates:${visaStarterId(visa)}` : base;
};

const newest = (a: OutgoingIntake, b: OutgoingIntake) =>
  (asDate(b.created_at)?.getTime() ?? 0) -
  (asDate(a.created_at)?.getTime() ?? 0);

/** The intake for this visa if the client has one, else their latest. */
export const pickIntake = (
  intakes: OutgoingIntake[],
  visa: string
): OutgoingIntake | null => {
  const live = intakes.filter((i) => !i.cancelled_at).sort(newest);
  const starter = visa ? visaStarterId(visa) : null;
  return (
    live.find((i) => starter && i.source_template_id === starter) ??
    live[0] ??
    null
  );
};

/**
 * Where Send Intake goes for this case: the client's intake in Requests when
 * one is out ("View Intake"), else Requests with the visa's starter. `to` is
 * null when the case has no client to send to.
 */
export type SendIntakeTarget = {
  to: string | null;
  label: "Send Intake" | "View Intake";
  /** Tooltip line: the intake's status, or why it can't go out. */
  hint: string;
};

const NO_CLIENT = "Link this case to a client to send them an intake";

export const sendIntakeTarget = (
  clientId: string | null,
  visa: string,
  intake: OutgoingIntake | null
): SendIntakeTarget => {
  if (!clientId) return { to: null, label: "Send Intake", hint: NO_CLIENT };
  if (intake)
    return {
      to: intakePath(clientId, intake.outgoing_intake_id),
      label: "View Intake",
      hint: `${intake.title || "Intake"} · ${displayStatus(intake)}`
    };
  return {
    to: sendIntakePath(clientId, visa),
    label: "Send Intake",
    hint: visa
      ? `Ask the client for documents with the ${visa} intake`
      : "Ask the client for documents"
  };
};

/** The case's Send Intake target; looks the client's intakes up once. */
export const useSendIntake = (): SendIntakeTarget => {
  const rawVisa = useCaseVisa();
  const visa = rawVisa === VISAVALUE.EMPTY ? "" : rawVisa || "";
  const clientId = useMatterOptional()?.foreignNationalId ?? null;
  const [intake, setIntake] = useState<OutgoingIntake | null>(null);
  useEffect(() => {
    setIntake(null);
    if (!clientId) return undefined;
    let cancelled = false;
    OutgoingIntakesAPI.listForForeignNational(clientId)
      .then((list) => {
        if (!cancelled) setIntake(pickIntake(list, visa));
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [clientId, visa]);
  return sendIntakeTarget(clientId, visa, intake);
};

/** The Case Files toolbar's Send Intake shortcut. */
export const SendIntakeIconButton: FC<{ target: SendIntakeTarget }> = ({
  target
}) => (
  <Tooltip>
    <TooltipTrigger asChild>
      {target.to ? (
        <Button
          variant="ghost"
          size="icon"
          className="size-6"
          asChild
          data-testid="case-files-send-intake"
        >
          <Link to={target.to} aria-label={target.label}>
            <Send className="size-3.5" />
          </Link>
        </Button>
      ) : (
        <span className="inline-flex">
          <Button
            variant="ghost"
            size="icon"
            className="size-6"
            aria-label={target.label}
            disabled
            data-testid="case-files-send-intake"
          >
            <Send className="size-3.5" />
          </Button>
        </span>
      )}
    </TooltipTrigger>
    <TooltipContent side="bottom">
      {target.label} · {target.hint}
    </TooltipContent>
  </Tooltip>
);

/** Send Intake in the "…" menu, when the toolbar has no room for it. */
export const SendIntakeMenuItem: FC<{ target: SendIntakeTarget }> = ({
  target
}) =>
  target.to ? (
    <DropdownMenuItem asChild data-testid="case-files-more-send-intake">
      <Link to={target.to}>
        <Send />
        {target.label}
      </Link>
    </DropdownMenuItem>
  ) : (
    <DropdownMenuItem disabled data-testid="case-files-more-send-intake">
      <Send />
      {target.label}
    </DropdownMenuItem>
  );
